import {
  CERTIFICATE_PALETTES,
  CERTIFICATE_TEMPLATES,
  defaultCertificate,
  paletteOf,
} from "@/utils/certificateModel";

const MOTTO_INDEX = 1;

function headerToApi(rows) {
  const fr = [];
  const en = [];
  (rows || []).forEach((row) => {
    const left = String(row.fr || "").trim();
    const right = String(row.en || "").trim();
    if (!left && !right) return;
    fr.push(left);
    en.push(right);
  });
  return [{ fr, en }];
}

export function toApiPayload(certificate) {
  const cert = certificate || {};
  return {
    template: cert.template,
    color: paletteOf(cert.palette).accent,
    header: headerToApi(cert.header_rows),
    title: cert.title || "",
    body: cert.body || "",
    signature: {
      place: cert.place || "",
      date: cert.date || "",
      role: cert.signatory_role || "",
      name: cert.signatory_name || "",
    },
    // Champ booléen exprimé en 0/1 côté API.
    space_for_steal: cert.show_seal ? 1 : 0,
  };
}
function paletteFromColor(color) {
  if (!color) return null;
  const value = String(color).trim().toLowerCase();
  const match = CERTIFICATE_PALETTES.find((p) => p.accent.toLowerCase() === value);
  return match ? match.id : null;
}

function parsePythonLiteral(text) {
  const s = text;
  let i = 0;

  const skipSpace = () => {
    while (i < s.length && /\s/.test(s[i])) i += 1;
  };

  const parseString = () => {
    const quote = s[i];
    i += 1;
    let out = "";
    while (i < s.length && s[i] !== quote) {
      if (s[i] === "\\" && i + 1 < s.length) {
        out += s[i + 1];
        i += 2;
      } else {
        out += s[i];
        i += 1;
      }
    }
    i += 1; // guillemet fermant
    return out;
  };

  const parseList = () => {
    i += 1; // [
    const out = [];
    skipSpace();
    if (s[i] === "]") {
      i += 1;
      return out;
    }
    for (;;) {
      out.push(parseValue());
      skipSpace();
      if (s[i] === ",") {
        i += 1;
        skipSpace();
        continue;
      }
      break;
    }
    skipSpace();
    if (s[i] === "]") i += 1;
    return out;
  };

  const parseDict = () => {
    i += 1; // {
    const out = {};
    skipSpace();
    if (s[i] === "}") {
      i += 1;
      return out;
    }
    for (;;) {
      skipSpace();
      const key = parseValue();
      skipSpace();
      if (s[i] === ":") i += 1;
      out[key] = parseValue();
      skipSpace();
      if (s[i] === ",") {
        i += 1;
        continue;
      }
      break;
    }
    skipSpace();
    if (s[i] === "}") i += 1;
    return out;
  };

  function parseValue() {
    skipSpace();
    const c = s[i];
    if (c === "[") return parseList();
    if (c === "{") return parseDict();
    if (c === "'" || c === '"') return parseString();
    // None / True / False / nombres : non attendus dans ce champ, mais
    // traités pour ne pas planter si jamais rencontrés.
    let start = i;
    while (i < s.length && !/[,\]}\s]/.test(s[i])) i += 1;
    const word = s.slice(start, i);
    if (word === "None") return null;
    if (word === "True") return true;
    if (word === "False") return false;
    const num = Number(word);
    return Number.isNaN(num) ? word : num;
  }

  return parseValue();
}

function headerFromApi(header) {
  let value = header;
  if (typeof value === "string") {
   
    try {
      value = JSON.parse(value);
    } catch (jsonError) {
      try {
        value = parsePythonLiteral(value);
      } catch (pyError) {
        return null;
      }
    }
  }

  const block = Array.isArray(value) ? value[0] : value;
  if (!block) return null;

  const fr = Array.isArray(block.fr) ? block.fr : [];
  const en = Array.isArray(block.en) ? block.en : [];
  const height = Math.max(fr.length, en.length);
  if (!height) return null;

  const rows = [];
  for (let i = 0; i < height; i += 1) {
    rows.push({
      id: `row-api-${i}`,
      fr: fr[i] || "",
      en: en[i] || "",
      italic: i === MOTTO_INDEX,
    });
  }
  return rows;
}

export function fromApiPayload(data, locale) {
  const base = defaultCertificate(locale);
  if (!data || typeof data !== "object") return base;

  const signature = data.signature || {};
  const rows = headerFromApi(data.header);
  const palette = paletteFromColor(data.color);
  const template = CERTIFICATE_TEMPLATES.includes(data.template) ? data.template : base.template;

  return {
    ...base,
    template,
    palette: palette || base.palette,
   
    custom_color: palette ? "" : data.color || "",
    header_rows: rows || base.header_rows,
    title: data.title || base.title,
    body: data.body || base.body,
    place: signature.place || "",
    date: signature.date || "",
    signatory_role: signature.role || base.signatory_role,
    signatory_name: signature.name || "",
    show_seal: Number(data.space_for_steal) === 1,
   
    logo_url: "",
  };
}

/** Identifiant de la ressource, quel que soit le nom du champ renvoyé. */
export function extractConfigId(data) {
  if (!data || typeof data !== "object") return null;
  return data.id || data._id || data.pk || null;
}
